import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button } from '@/Atomic/Molecules/Button'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import { Modal } from '@/Atomic/Organisms/Modal'
import { Form, FormBody, FormFields } from '@/Atomic/Templates/Form'
import { Document } from '@/Data/Document/DocumentDataClass'
import { useDocumentContext } from '@/hooks/documents/useDocumentHandler'
import { useModalHandler } from '@/hooks/useModalHandler'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import { getDataClassFormMap } from '@/utils/CRUD/read/dataClass'
import './NewFolderModal.scss'

export interface NewFolderModalProps {
  isOpen?: boolean
  close?: () => void
}

export const NewFolderModal: React.FC<NewFolderModalProps> = () => {
  const { t } = useTranslation()

  const { isOpen, openModal, closeModal } = useModalHandler()
  const { createFolder } = useDocumentContext()
  const { methods } = useDataClassFormHandler(Document)
  const { handleSubmit, reset, getValues, watch } = methods
  const groups = getDataClassFormMap(Document, getValues, watch)
  const titleField = groups
    .flatMap((group) => group.fields)
    .find((field) => field.name === 'title')

  const handleAdd = () => {
    handleSubmit(async (data) => {
      await createFolder(data)
      reset()
      closeModal()
    })()
  }

  const handleCancel = () => {
    reset()
    closeModal()
  }

  return (
    <>
      <Button
        variant="outlined"
        onClick={openModal}
        size="sm"
        icon="folder-add"
        title={t('components.document.newFolder')}
      />
      <Modal
        className="new-folder-modal"
        onClose={handleCancel}
        isOpen={isOpen}
      >
        <Modal.Header title={t('components.document.newFolder')} />
        <Modal.Body>
          <FormProvider {...methods}>
            <Form onSubmit={handleAdd}>
              <FormBody>
                <FormFields>
                  {titleField && (
                    <CustomField
                      fieldMap={titleField}
                      isEditing
                      className="form-field"
                    />
                  )}
                </FormFields>
              </FormBody>
            </Form>
          </FormProvider>
        </Modal.Body>
        <Modal.Footer
          onCancelText={t('common.cancel')}
          onCancel={handleCancel}
          onConfirmText={t('components.document.createNewFolder')}
          onConfirm={handleAdd}
        />
      </Modal>
    </>
  )
}
